import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Select from '../../components/forms/Select';
import TextArea from '../../components/forms/TextArea';
import { useLead, useUpdateLead, useDeleteLead, useConvertLead } from '../../hooks/api/useLeads';
import { useToast } from '../../components/ui/ToastProvider';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { formatDateTime } from '../../utils/date';

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-purple-100 text-purple-700',
  converted: 'bg-green-100 text-green-700',
  unsubscribed: 'bg-gray-100 text-gray-700',
};

const LeadDetail: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: lead, isLoading } = useLead(leadId || '', !!leadId);
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const convertLead = useConvertLead();

  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');

  if (isLoading) {
    return <div className="p-6 text-center text-gray-500">Loading lead...</div>;
  }

  if (!lead) {
    return <div className="p-6 text-center text-gray-500">Lead not found</div>;
  }

  const handleStatusUpdate = async () => {
    if (!newStatus && !notes) return;
    try {
      await updateLead.mutateAsync({
        id: lead.id,
        data: {
          ...(newStatus ? { status: newStatus } : {}),
          ...(notes ? { notes } : {}),
        },
      });
      addToast({ type: 'success', title: 'Lead updated' });
      setNotes('');
      setNewStatus('');
    } catch {
      addToast({ type: 'error', title: 'Failed to update lead' });
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure? This will permanently delete this lead and all associated data (POPIA right to erasure).')) return;
    try {
      await deleteLead.mutateAsync(lead.id);
      addToast({ type: 'success', title: 'Lead deleted' });
      navigate('/admin/leads');
    } catch {
      addToast({ type: 'error', title: 'Failed to delete lead' });
    }
  };

  const handleConvert = async () => {
    const orgName = lead.school_name || prompt('Enter organization name:');
    if (!orgName) return;
    try {
      const result = await convertLead.mutateAsync({
        id: lead.id,
        data: { org_name: orgName, admin_email: lead.email },
      });
      addToast({ type: 'success', title: `Lead converted! Organization ID: ${result.organization_id}` });
    } catch {
      addToast({ type: 'error', title: 'Failed to convert lead' });
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/admin/leads')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back to leads
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-xl font-display font-bold text-gray-900">
                    {lead.first_name || lead.last_name
                      ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
                      : lead.email}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">{lead.email}</p>
                </div>
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full capitalize ${
                  statusColors[lead.status] || 'bg-gray-100 text-gray-700'
                }`}>
                  {lead.status}
                </span>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                {lead.phone && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Phone</p>
                    <p className="text-sm text-gray-900">{lead.phone}</p>
                  </div>
                )}
                {lead.school_name && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">School</p>
                    <p className="text-sm text-gray-900">{lead.school_name}</p>
                  </div>
                )}
                {lead.role && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Role</p>
                    <p className="text-sm text-gray-900 capitalize">{lead.role}</p>
                  </div>
                )}
                {lead.num_children && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Children</p>
                    <p className="text-sm text-gray-900">{lead.num_children}</p>
                  </div>
                )}
                {lead.province && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Province</p>
                    <p className="text-sm text-gray-900">{lead.province}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Source</p>
                  <p className="text-sm text-gray-900">{lead.source}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Form Type</p>
                  <p className="text-sm text-gray-900 capitalize">{lead.form_type}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Date</p>
                  <p className="text-sm text-gray-900">{formatDateTime(lead.created_date)}</p>
                </div>
              </div>

              {lead.message && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Message</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.message}</p>
                </div>
              )}

              {lead.notes && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Notes</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.notes}</p>
                </div>
              )}
            </Card>

            {/* Activity Timeline */}
            <Card>
              <h2 className="text-lg font-display font-semibold text-gray-900 mb-4">Activity</h2>
              {!lead.activities || lead.activities.length === 0 ? (
                <p className="text-sm text-gray-500">No activity recorded yet.</p>
              ) : (
                <div className="space-y-4">
                  {lead.activities.map(activity => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className="w-2 h-2 mt-2 rounded-full bg-primary-400 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium capitalize">{activity.activity_type.replace('_', ' ')}</span>
                          {activity.description && ` — ${activity.description}`}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDateTime(activity.created_date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Update Status */}
            <Card>
              <h3 className="font-medium text-gray-900 mb-4">Update Lead</h3>
              <div className="space-y-3">
                <Select
                  label="Status"
                  value={newStatus || lead.status}
                  onChange={e => setNewStatus(e.target.value)}
                  options={statusOptions}
                />
                <TextArea
                  label="Add a note"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Called, interested in trial..."
                  rows={3}
                />
                <Button
                  variant="primary"
                  fullWidth
                  onClick={handleStatusUpdate}
                  isLoading={updateLead.isPending}
                  disabled={!newStatus && !notes}
                >
                  Save Changes
                </Button>
              </div>
            </Card>

            {/* Convert */}
            {lead.status !== 'converted' && (
              <Card>
                <h3 className="font-medium text-gray-900 mb-2">Convert to Organization</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Create an organization and user account for this lead.
                </p>
                <Button
                  variant="success"
                  fullWidth
                  onClick={handleConvert}
                  isLoading={convertLead.isPending}
                >
                  Convert Lead
                </Button>
              </Card>
            )}

            {/* Delete (POPIA) */}
            <Card>
              <h3 className="font-medium text-gray-900 mb-2">POPIA Right to Erasure</h3>
              <p className="text-xs text-gray-500 mb-4">
                Permanently delete this lead and all associated data.
              </p>
              <Button
                variant="danger"
                fullWidth
                onClick={handleDelete}
                isLoading={deleteLead.isPending}
              >
                Delete Lead
              </Button>
            </Card>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LeadDetail;
